import { useCallback, useRef, useState } from 'react';

/**
 * Estado de filtros de UMA aba de relatório, persistido em `sessionStorage`
 * (sobrevive à troca de aba e a recarregar a página; some ao fechar a
 * janela/aba do navegador).
 *
 * ```ts
 * const [filtros, setFiltro, limpar] = useFiltrosRelatorio('pecas', {
 *   status: 'exceto-canceladas',
 *   colaborador: '',
 * });
 * setFiltro('colaborador', id);
 * limpar(); // volta ao padrão
 * ```
 *
 * - `aba` é a chave de persistência (`relatorios.filtros.<aba>`). Trocar
 *   `aba` recarrega os valores daquela aba.
 * - Os valores salvos são mesclados sobre `padrao`: chave nova no padrão
 *   aparece; chave removida do padrão é ignorada.
 * - Só guarde valores serializáveis em JSON (strings, números, booleanos).
 * - Falha de `sessionStorage` (modo privado, bloqueado) é ignorada — o
 *   filtro continua funcionando só em memória.
 *
 * Os `useState` atuais da `RelatoriosPage` NÃO foram migrados para cá: a
 * adoção é opcional, aba a aba.
 */

const PREFIXO = 'relatorios.filtros.';

function ler<T extends object>(aba: string, padrao: T): T {
  try {
    const bruto = window.sessionStorage.getItem(PREFIXO + aba);
    if (!bruto) return padrao;
    const salvo = JSON.parse(bruto) as Record<string, unknown>;
    if (!salvo || typeof salvo !== 'object') return padrao;
    const resultado = { ...padrao } as Record<string, unknown>;
    for (const chave of Object.keys(padrao)) {
      if (chave in salvo) resultado[chave] = salvo[chave];
    }
    return resultado as T;
  } catch {
    return padrao;
  }
}

function gravar<T extends object>(aba: string, valores: T): void {
  try {
    window.sessionStorage.setItem(PREFIXO + aba, JSON.stringify(valores));
  } catch {
    // Sem armazenamento: segue só em memória.
  }
}

function apagar(aba: string): void {
  try {
    window.sessionStorage.removeItem(PREFIXO + aba);
  } catch {
    // idem
  }
}

export function useFiltrosRelatorio<T extends object>(
  aba: string,
  padrao: T,
): [T, <K extends keyof T>(chave: K, valor: T[K]) => void, () => void] {
  // O padrão costuma vir como literal (objeto novo a cada render); usar o
  // primeiro evita recarregar à toa.
  const padraoRef = useRef(padrao);
  const [estado, setEstado] = useState(() => ({ aba, valores: ler(aba, padraoRef.current) }));

  let atual = estado;
  if (estado.aba !== aba) {
    atual = { aba, valores: ler(aba, padraoRef.current) };
    setEstado(atual);
  }

  const setFiltro = useCallback(
    <K extends keyof T>(chave: K, valor: T[K]) => {
      setEstado((anterior) => {
        const valores = { ...anterior.valores, [chave]: valor };
        gravar(anterior.aba, valores);
        return { aba: anterior.aba, valores };
      });
    },
    [],
  );

  const limpar = useCallback(() => {
    setEstado((anterior) => {
      apagar(anterior.aba);
      return { aba: anterior.aba, valores: padraoRef.current };
    });
  }, []);

  return [atual.valores, setFiltro, limpar];
}
